"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  InputBase,
  InputAdornment,
  Switch,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Add from "@material-ui/icons/Add";
import Close from "@material-ui/icons/Close";
import Remove from "@material-ui/icons/Remove";
import { useQueryClient, useMutation } from "@tanstack/react-query";

import useMonetization from "../../../../hooks/useMonetization";
import { useSubscriptionPlansQuery } from "../../../../query/hooks/useSubscriptionPlansQuery";
import { useSubscriptionStyles } from "../WelcomeMessage/style";
import {
  createSubscriptionPlanAPI,
  updateSubscriptionPlanAPI,
  deleteSubscriptionPlanAPI,
  getUserPersonalInfo,
  editProfileDataAPI,
  patchMySubscriptionSettingsAPI,
} from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { queryKeys } from "../../../../query/keys";
import trashIconSBUS from "../../../../assets/images/icons/trashIconSBUS.svg";
import pencilIcon from "../../../../assets/images/icons/pencilIcon.svg";
import markIcon from "../../../../assets/svgs/markIcon.svg";
import { ReactComponent as TrashIcon } from "../../../../assets/images/icons/trashIcon.svg";
import { clsx } from 'clsx';
import { AttachMoney } from "@material-ui/icons";
import { DollarSign } from "lucide-react";

const MAX_PLANS = 3;

const DEFAULT_FREE_PLAN_BENEFITS = [
  "Basic content access",
  "DM access",
  "Custom video request",
];

const SUBSCRIPTION_LIMIT_BANNER_TEXT =
  "You can only use 3 types of subscription at once!";

function subscriptionErrorMessage(err: unknown): string {
  const e = err as any;
  const raw = e?.message ?? e?.error ?? (typeof e === "string" ? e : "") ?? "";
  return String(raw).trim();
}

function isSubscriptionLimitError(err: unknown): boolean {
  const msg = subscriptionErrorMessage(err).toLowerCase();
  if (!msg) return false;
  const mentionsMaxThree =
    msg.includes("maximum") && (msg.includes("3") || msg.includes("three"));
  const mentionsDeactivate =
    msg.includes("deactivate") &&
    (msg.includes("plan") || msg.includes("free"));
  return (
    mentionsMaxThree ||
    msg.includes("3 types") ||
    msg.includes("3 active") ||
    msg.includes("active offers") ||
    mentionsDeactivate
  );
}

const TRIAL_SEGMENT_STYLE = {
  week: {
    width: "max-content",
    minWidth: 107,
    maxWidth: 124,
    boxSizing: "border-box" as const,
    padding: "6px 12px",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600 as const,
    fontSize: 13,
    borderRadius: 6,
  },
  month: {
    width: "max-content",
    minWidth: 107,
    maxWidth: 124,
    boxSizing: "border-box" as const,
    padding: "6px 12px",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600 as const,
    fontSize: 13,
    borderRadius: 6,
  },
  off: {
    width: "max-content",
    minWidth: 107,
    maxWidth: 124,
    boxSizing: "border-box" as const,
    padding: "6px 12px",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600 as const,
    fontSize: 13,
    borderRadius: 6,
  },
};

/** Query cache shape from fetchMySubscriptionPlans: { items, meta } (not a bare array). */
function patchSubscriptionPlansCache(
  old: unknown,
  patchItems: (items: any[]) => any[]
): unknown {
  if (old == null) return old;
  if (Array.isArray(old)) {
    return patchItems(old);
  }
  const o = old as { items?: any[]; meta?: unknown };
  const items = Array.isArray(o.items) ? o.items : [];
  return { ...o, items: patchItems(items) };
}

const Monetization: React.FC = () => {
  const classes = useSubscriptionStyles() as any;
  const { loading } = useMonetization();
  const queryClient = useQueryClient();
  const { data: plansData, isLoading: plansLoading } =
    useSubscriptionPlansQuery();
  const plans = useMemo(() => {
    return (
      (plansData as any)?.items ?? (Array.isArray(plansData) ? plansData : [])
    );
  }, [plansData]);
  const plansMeta = useMemo(() => {
    return (plansData as any)?.meta ?? {};
  }, [plansData]);
  const theme = useTheme();
  const isMobileSM = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const tipAmountOptions = useMemo(() => [1, 2, 3, 5, 10, 15, 20, 25, 50], []);
  const videoPriceOptions = useMemo(
    () => [5, 10, 15, 20, 25, 50, 75, 100, 150, 200],
    []
  );
  const [pricingLoading, setPricingLoading] = useState(true);
  const [minimumTipAmount, setMinimumTipAmount] = useState<number>(5);
  const [videoRequestPrice, setVideoRequestPrice] = useState<number>(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    price: "",
    currency: "USD",
    memberBenefits: "",
    sortOrder: 0,
  });
  const [primaryPeriod, setPrimaryPeriod] = useState<"week" | "month">("week");
  const [showSecondPeriod, setShowSecondPeriod] = useState(false);
  const [secondaryPrice, setSecondaryPrice] = useState("");
  const [secondaryPeriod, setSecondaryPeriod] = useState<"week" | "month">(
    "week"
  );
  const [trialPeriod, setTrialPeriod] = useState<"week" | "month" | "off">(
    "off"
  );
  const [benefits, setBenefits] = useState<string[]>([]);
  const [newBenefit, setNewBenefit] = useState("");
  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const [showSubscriptionLimitBanner, setShowSubscriptionLimitBanner] =
    useState(false);
  const [planPendingDelete, setPlanPendingDelete] = useState<any | null>(null);
  /** Після вимкнення trial зберігаємо week/month, щоб увімкнути знову без edit */
  const lastTrialPeriodByPlanIdRef = useRef<Record<string, "week" | "month">>(
    {}
  );

  const createMutation = useMutation({
    mutationFn: createSubscriptionPlanAPI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans });
      showToast("Plan created", "success");
      setModalOpen(false);
    },
    onError: (e: Error) => {
      if (isSubscriptionLimitError(e)) {
        setShowSubscriptionLimitBanner(true);
      } else {
        showToast(
          subscriptionErrorMessage(e) || "Failed to create plan",
          "error"
        );
      }
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateSubscriptionPlanAPI(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.subscriptionPlans,
      });
      const previousPlans = queryClient.getQueryData(
        queryKeys.subscriptionPlans
      );

      queryClient.setQueryData(queryKeys.subscriptionPlans, old =>
        patchSubscriptionPlansCache(old, current =>
          current.map(plan => (plan._id === id ? { ...plan, ...data } : plan))
        )
      );

      return { previousPlans };
    },
    onError: (err, _vars, context) => {
      if (context?.previousPlans) {
        queryClient.setQueryData(
          queryKeys.subscriptionPlans,
          context.previousPlans
        );
      }
      if (isSubscriptionLimitError(err)) {
        setShowSubscriptionLimitBanner(true);
      } else {
        showToast(
          subscriptionErrorMessage(err) || "Failed to update subscription plan",
          "error"
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans });
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: (id: string) => deleteSubscriptionPlanAPI(id),
    onMutate: async id => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.subscriptionPlans,
      });
      const previousPlans = queryClient.getQueryData(
        queryKeys.subscriptionPlans
      );

      queryClient.setQueryData(queryKeys.subscriptionPlans, old =>
        patchSubscriptionPlansCache(old, current =>
          current.filter(plan => plan._id !== id)
        )
      );

      return { previousPlans };
    },
    onError: (err, _id, context) => {
      if (context?.previousPlans) {
        queryClient.setQueryData(
          queryKeys.subscriptionPlans,
          context.previousPlans
        );
      }
      showToast(
        (err as Error)?.message || "Failed to delete subscription plan",
        "error"
      );
    },
    onSuccess: () => {
      setPlanPendingDelete(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans });
    },
  });

  const updatePricingMutation = useMutation({
    mutationFn: (formData: FormData) => editProfileDataAPI(formData),
    onError: (e: any) => {
      showToast(e?.message || "Failed to update pricing", "error");
    },
  });

  const freePlan = useMemo(
    () => plans.find((p: any) => p?.isFree || p?._id === "free") ?? null,
    [plans]
  );
  const freePlanDisplayTitle = useMemo(() => {
    if (!freePlan) return "Free subscription";
    const raw = String(freePlan.name || "Free").trim();
    return /subscription\s*$/i.test(raw) ? raw : `${raw} subscription`;
  }, [freePlan]);
  const freePlanCardBenefits = useMemo(() => {
    if (!freePlan) return DEFAULT_FREE_PLAN_BENEFITS;
    const fromApi = freePlan.memberBenefits;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi;
    }
    return DEFAULT_FREE_PLAN_BENEFITS;
  }, [freePlan]);
  /** Label for “$0.00 / …” — same pattern as paid plan rows */
  const freePlanPricePeriodLabel = useMemo(() => {
    const p = String((freePlan as any)?.period || "month").toLowerCase();
    if (p === "week" || p === "month") return p;
    return "month";
  }, [freePlan]);
  const paidPlans = useMemo(
    () => plans.filter((p: any) => !(p?.isFree || p?._id === "free")),
    [plans]
  );
  const freeEnabled = Boolean((plansMeta as any)?.freeEnabled);
  const activeCount = useMemo(() => {
    const activePaid = paidPlans.filter((plan: any) => !!plan.isActive).length;
    return activePaid + (freeEnabled ? 1 : 0);
  }, [paidPlans, freeEnabled]);

  useEffect(() => {
    paidPlans.forEach((p: any) => {
      if (p?.trialPeriod === "week" || p?.trialPeriod === "month") {
        lastTrialPeriodByPlanIdRef.current[p._id] = p.trialPeriod;
      }
    });
  }, [paidPlans]);

  useEffect(() => {
    if (activeCount < MAX_PLANS) {
      setShowSubscriptionLimitBanner(false);
    }
  }, [activeCount]);

  const subscriptionSettingsMutation = useMutation({
    mutationFn: (payload: any) => patchMySubscriptionSettingsAPI(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans });
    },
    onError: (e: any) => {
      if (isSubscriptionLimitError(e)) {
        setShowSubscriptionLimitBanner(true);
      } else {
        showToast(
          subscriptionErrorMessage(e) ||
          "Failed to update subscription settings",
          "error"
        );
      }
    },
  });

  const [editingFree, setEditingFree] = useState(false);
  const [freeFormName, setFreeFormName] = useState("");
  const [freeFormBenefits, setFreeFormBenefits] = useState<string[]>([]);
  const [freeBenefitDraft, setFreeBenefitDraft] = useState("");

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setPricingLoading(true);
      try {
        const res = await getUserPersonalInfo();
        const data = (res as any)?.data ?? (res as any);
        const minTip = Number(data?.minimumTipAmount ?? 5);
        const videoPrice = Number(data?.customContentPrice ?? 5);

        if (!mounted) return;
        setMinimumTipAmount(Number.isFinite(minTip) ? minTip : 5);
        setVideoRequestPrice(Number.isFinite(videoPrice) ? videoPrice : 5);
      } catch (e: any) {
        showToast(e?.message || "Failed to load pricing", "error");
      } finally {
        if (mounted) setPricingLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, []);

  const saveMinimumTipAmount = (value: number) => {
    setMinimumTipAmount(value);
    const formData = new FormData();
    formData.append("minimumTipAmount", String(value));
    updatePricingMutation.mutate(formData, {
      onSuccess: (res: any) => {
        const ok = res?.success ?? true;
        if (ok) {
          showToast("Tip minimum updated", "success");
        } else {
          showToast(res?.message || "Failed to update tip minimum", "error");
        }
      },
    });
  };

  const saveVideoRequestPrice = (value: number) => {
    setVideoRequestPrice(value);
    const formData = new FormData();
    formData.append("customContentPrice", String(value));
    updatePricingMutation.mutate(formData, {
      onSuccess: () => {
        showToast("Video request price updated", "success");
      },
    });
  };

  const handleOpenModal = () => {
    setEditingPlan(null);
    setEditingFree(false);
    setForm({
      name: "",
      price: "",
      currency: "USD",
      memberBenefits: "",
      sortOrder: paidPlans.length,
    });
    setPrimaryPeriod("week");
    setShowSecondPeriod(false);
    setSecondaryPrice("");
    setSecondaryPeriod("week");
    setTrialPeriod("off");
    setBenefits([]);
    setNewBenefit("");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!createMutation.isPending && !updatePlanMutation.isPending) {
      setModalOpen(false);
    }
  };

  const handleCreatePlan = () => {
    const name = form.name.trim();
    const price = parseFloat(form.price);
    if (!name) {
      showToast("Enter plan name", "error");
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      showToast("Enter a valid price", "error");
      return;
    }
    const memberBenefitsRaw = form.memberBenefits
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean);
    const mergedBenefits = [...benefits, ...memberBenefitsRaw];
    const payload: any = {
      name,
      price,
      currency: form.currency,
      // Використовуємо обраний primaryPeriod, а не завжди "month"
      period: primaryPeriod,
      trialPeriod,
      memberBenefits: mergedBenefits.length ? mergedBenefits : undefined,
      sortOrder: form.sortOrder,
    };

    // Якщо додано другий період, передаємо його як окрему ціну
    const secondPriceNumber = parseFloat(secondaryPrice);
    if (
      showSecondPeriod &&
      !Number.isNaN(secondPriceNumber) &&
      secondPriceNumber >= 0
    ) {
      payload.secondaryPrice = secondPriceNumber;
      payload.secondaryPeriod = secondaryPeriod; // "week" або "month"
    }

    createMutation.mutate(payload);
  };

  const handleSaveEditedPlan = () => {
    if (!editingPlan?._id) return;

    const name = form.name.trim();
    const price = parseFloat(form.price);
    if (!name) {
      showToast("Enter plan name", "error");
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      showToast("Enter a valid price", "error");
      return;
    }

    const memberBenefitsRaw = form.memberBenefits
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean);
    const mergedBenefits = [...benefits, ...memberBenefitsRaw];

    const payload: any = {
      name,
      price,
      currency: form.currency,
      period: primaryPeriod,
      trialPeriod,
      memberBenefits: mergedBenefits.length ? mergedBenefits : undefined,
      sortOrder: form.sortOrder,
    };

    const secondPriceNumber = parseFloat(secondaryPrice);
    if (
      showSecondPeriod &&
      !Number.isNaN(secondPriceNumber) &&
      secondPriceNumber >= 0
    ) {
      payload.secondaryPrice = secondPriceNumber;
      payload.secondaryPeriod = secondaryPeriod;
    }

    updatePlanMutation.mutate(
      { id: editingPlan._id, data: payload },
      {
        onSuccess: () => {
          showToast("Plan updated", "success");
          setEditingPlan(null);
          setBenefits([]);
          setNewBenefit("");
        },
      }
    );
  };

  const handleAddBenefit = () => {
    const text = newBenefit.trim();
    if (!text) return;
    setBenefits(prev => [...prev, text]);
    setNewBenefit("");
  };

  const handlePrimaryPeriodChange = (period: "week" | "month") => {
    if (period === secondaryPeriod) {
      setSecondaryPeriod(primaryPeriod);
      setPrimaryPeriod(period);
    } else {
      setPrimaryPeriod(period);
    }
  };

  const handleSecondaryPeriodChange = (period: "week" | "month") => {
    if (period === primaryPeriod) {
      setPrimaryPeriod(secondaryPeriod);
      setSecondaryPeriod(period);
    } else {
      setSecondaryPeriod(period);
    }
  };

  const handleToggleActive = (plan: any) => {
    if (!plan?._id) return;
    if (!plan.isActive && activeCount >= MAX_PLANS) {
      setShowSubscriptionLimitBanner(true);
      return;
    }
    updatePlanMutation.mutate({
      id: plan._id,
      data: { isActive: !plan.isActive },
    });
  };

  /** Увімк/вимк trial без режиму edit: PATCH лише trialPeriod */
  const handleTrialQuickToggle = (plan: any, checked: boolean) => {
    if (!plan?._id || updatePlanMutation.isPending) return;
    if (checked) {
      const trial =
        lastTrialPeriodByPlanIdRef.current[plan._id] ??
        (plan.trialPeriod === "week" || plan.trialPeriod === "month"
          ? plan.trialPeriod
          : "week");
      updatePlanMutation.mutate({ id: plan._id, data: { trialPeriod: trial } });
    } else {
      updatePlanMutation.mutate({
        id: plan._id,
        data: { trialPeriod: "off" },
      });
    }
  };

  const handleStartEditFree = () => {
    if (!freePlan) return;
    setEditingPlan(null);
    setFreeFormName(String(freePlan.name || "Free"));
    const fromApi = freePlan.memberBenefits;
    setFreeFormBenefits(
      Array.isArray(fromApi) && fromApi.length > 0
        ? [...fromApi]
        : [...DEFAULT_FREE_PLAN_BENEFITS]
    );
    setFreeBenefitDraft("");
    setEditingFree(true);
  };

  const handleAddFreeBenefit = () => {
    const text = freeBenefitDraft.trim();
    if (!text) return;
    setFreeFormBenefits(prev => [...prev, text]);
    setFreeBenefitDraft("");
  };

  const handleSaveFreePlan = () => {
    const name = freeFormName.trim();
    if (!name) {
      showToast("Enter plan name", "error");
      return;
    }
    if (subscriptionSettingsMutation.isPending) return;
    subscriptionSettingsMutation.mutate(
      { freePlan: { name, memberBenefits: freeFormBenefits } },
      {
        onSuccess: () => {
          showToast("Free plan updated", "success");
          setEditingFree(false);
          setFreeBenefitDraft("");
        },
      }
    );
  };

  const handleEditPlan = (plan: any) => {
    if (!plan) return;
    setEditingFree(false);
    setEditingPlan(plan);
    setForm({
      name: plan.name ?? "",
      price: plan.price != null ? String(plan.price) : "",
      currency: plan.currency ?? "USD",
      memberBenefits: "",
      sortOrder: plan.sortOrder ?? 0,
    });
    setPrimaryPeriod(plan.period === "month" ? "month" : "week");
    setTrialPeriod(
      plan?.trialPeriod === "week" || plan?.trialPeriod === "month"
        ? plan.trialPeriod
        : "off"
    );
    if (plan.secondaryPrice != null) {
      setShowSecondPeriod(true);
      setSecondaryPrice(String(plan.secondaryPrice));
      setSecondaryPeriod(plan.secondaryPeriod ?? "month");
    } else {
      setShowSecondPeriod(false);
      setSecondaryPrice("");
      setSecondaryPeriod("month");
    }
    setBenefits(
      Array.isArray(plan.memberBenefits) ? [...plan.memberBenefits] : []
    );
  };

  const handleRequestDeletePlan = (plan: any) => {
    if (!plan?._id) return;
    setPlanPendingDelete(plan);
  };

  const handleCloseDeletePlanDialog = () => {
    if (deletePlanMutation.isPending) return;
    setPlanPendingDelete(null);
  };

  const handleConfirmDeletePlan = () => {
    if (!planPendingDelete?._id || deletePlanMutation.isPending) return;
    deletePlanMutation.mutate(planPendingDelete._id);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={classes.monetizationSection}>
      <Typography className={classes.sectionTitle}>
        Profile monetization
      </Typography>
      <Typography className={classes.monetizationSubtitle}>
        Set your content prices
      </Typography>

      <Card className={classes.tierCard}>
        <CardContent className={clsx(classes.cardContent, classes.cardContentMonetization)}>
          <Box className={clsx(classes.tierHeader, classes.tierHeaderMonetization)}>
            <Box>
              <Box className={classes.subscriptionCounter}>
                <Typography className={classes.subscriptionCounterLabel}>
                  Subscription
                </Typography>
                <Typography className={classes.subscriptionCounterValue}>
                  {plansLoading ? "–" : `${activeCount}/${MAX_PLANS}`}
                </Typography>
              </Box>
              <Typography className={classes.subscriptionHint}>
                You can only use {MAX_PLANS} types of subscription at once
              </Typography>
            </Box>

            <Button
              className={classes.createSubscriptionButton}
              onClick={handleOpenModal}
            >
              <Add style={{ fontSize: isMobile ? 20 : 24 }} />
              {!isMobile && "Create new subscription type"}
            </Button>
          </Box>

          {showSubscriptionLimitBanner && (
            <Box
              role="alert"
              style={{
                borderRadius: 4,
                backgroundColor: "#BE326526",
                padding: 12,
                marginTop: 16,
              }}
            >
              <Typography
                style={{
                  color: "#FFFFFF",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                {SUBSCRIPTION_LIMIT_BANNER_TEXT}
              </Typography>
            </Box>
          )}

          {!plansLoading && plans.length > 0 && (
            <Box className={classes.subscriptionList}>
              {freePlan && (
                <Box
                  key={freePlan._id || "free"}
                  className={`${classes.subscriptionItem} ${editingFree ? classes.subscriptionItemEditing : ""
                    } ${!freeEnabled ? classes.subscriptionItemInactive : ""}`}
                  style={{ flexWrap: "wrap", alignItems: isMobile ? "flex-start" : "center" }}
                >
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Box
                      display="flex"
                      className={`${classes.subscriptionItemRow}${editingFree ? ` ${classes.freePlanEditPlanRow}` : ""
                        }`}
                    >
                      <Box
                        className={`${classes.subscriptionItemLeft} ${editingFree ? classes.freePlanEditLeft : ""
                          }`}
                      >
                        <Box className={classes.memberBenefitsList}>
                          {!editingFree ? (
                            <Typography
                              className={classes.subscriptionItemName}
                              style={{ marginBottom: isMobile ? 0 : 16 }}
                            >
                              {freePlanDisplayTitle}
                            </Typography>
                          ) : (
                            <InputBase
                              value={freeFormName}
                              onChange={e => setFreeFormName(e.target.value)}
                              placeholder="Free"
                              fullWidth
                              className={classes.freePlanEditTitleInput}
                              inputProps={{
                                "aria-label": "Free plan name",
                              }}
                            />
                          )}
                        </Box>
                        {!editingFree ? (
                          <Box className={classes.memberBenefitsList}>
                            {freePlanCardBenefits.map(
                              (benefit: string, idx: number) => (
                                <Box
                                  key={`${benefit}-${idx}`}
                                  display="flex"
                                  alignItems="center"
                                  style={{ gap: 6 }}
                                >
                                  <img
                                    src={markIcon || "/placeholder.svg"}
                                    alt=""
                                    className={classes.subscriptionBenefitMark}
                                  />
                                  <Typography
                                    className={classes.subscriptionItemBenefits}
                                  >
                                    {benefit}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </Box>
                        ) : (
                          <Box>
                            <Box className={classes.inlineBenefitsEditor}>
                              {freeFormBenefits.length > 0 && (
                                <Box className={classes.benefitsList}>
                                  {freeFormBenefits.map((b, idx) => (
                                    <Box
                                      key={`${b}-${idx}`}
                                      className={classes.benefitItem}
                                    >
                                      <IconButton
                                        className={classes.removeBenefitButton}
                                        onClick={() =>
                                          setFreeFormBenefits(prev =>
                                            prev.filter((_, i) => i !== idx)
                                          )
                                        }
                                      >
                                        <Remove style={{ fontSize: 16, color: "#FF78AA" }} />
                                      </IconButton>
                                      <Typography
                                        className={classes.benefitText}
                                      >
                                        {b}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                              <Box
                                className={classes.benefitsInputRow}
                                style={{
                                  marginTop: freeFormBenefits.length ? 12 : 0,
                                }}
                              >
                                <Button
                                  className={classes.addBenefitsIconButton}
                                  onClick={handleAddFreeBenefit}
                                >
                                  <Add style={{ fontSize: 12 }} />
                                </Button>
                                <input
                                  className={classes.freePlanAddBenefitInput}
                                  placeholder="Add more benefits"
                                  value={freeBenefitDraft}
                                  onChange={e =>
                                    setFreeBenefitDraft(e.target.value)
                                  }
                                  onKeyDown={e => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleAddFreeBenefit();
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {editingFree && (
                        <Box className={classes.freePlanEditChipsCenterWrap}>
                          <Box className={classes.chipGroup}>
                            <Typography
                              component="span"
                              className={`${classes.freePlanPeriodChip} ${classes.freePlanPeriodChipActive}`}
                            >
                              Week
                            </Typography>
                            <Typography
                              component="span"
                              className={`${classes.freePlanPeriodChip} ${classes.freePlanPeriodChipInactive}`}
                            >
                              Month
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {!editingFree && (
                        <Box className={classes.subscriptionItemPriceRow}>
                          <Typography
                            className={classes.subscriptionPriceBadge}
                          >
                            <span className={classes.subscriptionPriceValue}>
                              $0.00
                            </span>
                            <span className={classes.subscriptionPricePeriod}>
                              {" "}
                              / {freePlanPricePeriodLabel}
                            </span>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box
                    className={`${classes.subscriptionItemRight} ${classes.subscriptionTrialPlanRight}`}
                    style={editingFree ? { alignItems: "center" } : {}}
                  >
                    <Box className={classes.subscriptionItemActions}>
                      {!editingFree && (
                        <Switch
                          color="primary"
                          size="small"
                          className={classes.subscriptionSwitch}
                          checked={freeEnabled}
                          onChange={() => {
                            const turningOn = !freeEnabled;
                            if (turningOn && activeCount >= MAX_PLANS) {
                              setShowSubscriptionLimitBanner(true);
                              return;
                            }
                            subscriptionSettingsMutation.mutate({
                              freeEnabled: !freeEnabled,
                            });
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        className={classes.subscriptionItemIconButton}
                        disabled={
                          editingFree && subscriptionSettingsMutation.isPending
                        }
                        onClick={() =>
                          editingFree
                            ? handleSaveFreePlan()
                            : handleStartEditFree()
                        }
                      >
                        <img
                          src={pencilIcon || "/placeholder.svg"}
                          width={isMobile ? 14 : 24}
                          height={isMobile ? 14 : 24}
                          alt={
                            editingFree ? "Save free plan" : "Edit free plan"
                          }
                        />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}

              {paidPlans.map((plan: any) => {
                const isEditing = editingPlan?._id === plan._id;

                return (
                  <Box
                    key={plan._id}
                    className={`${classes.subscriptionItem} ${isEditing ? classes.subscriptionItemEditing : ""
                      } ${!plan.isActive ? classes.subscriptionItemInactive : ""}`}
                    style={{ flexWrap: "wrap", alignItems: "center" }}
                  >
                    <Box style={{ minWidth: 0, maxWidth: isEditing ? "calc(100% - 52px)" : "none", flex: 1 }}>
                      <Box
                        display="flex"
                        className={classes.subscriptionItemRow} style={isEditing && isMobile ? { flexDirection: "column", alignItems: "flex-start" } : {}}
                      >
                        <Box className={classes.subscriptionItemLeft}>
                          <Box>
                            <Typography
                              className={classes.subscriptionItemName}
                            >
                              {plan.name}
                            </Typography>
                          </Box>
                          <Box className={classes.subscriptionItemOptions}>
                            {!plan.price && plan.price !== 0 && !isEditing && (
                              <Typography
                                className={classes.subscriptionItemTrialText}
                              >
                                After the trial, the user automatically switches
                                to the lowest-priced plan
                              </Typography>
                            )}
                            {!isEditing &&
                              Array.isArray(plan.memberBenefits) &&
                              plan.memberBenefits.length > 0 && (
                                <Box className={classes.memberBenefitsList}>
                                  {plan.memberBenefits.map(
                                    (benefit: string) => (
                                      <Box
                                        key={benefit}
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: 6 }}
                                      >
                                        <img
                                          src={markIcon || "/placeholder.svg"}
                                          alt=""
                                          className={
                                            classes.subscriptionBenefitMark
                                          }
                                        />
                                        <Typography
                                          className={
                                            classes.subscriptionItemBenefits
                                          }
                                        >
                                          {benefit}
                                        </Typography>
                                      </Box>
                                    )
                                  )}
                                </Box>
                              )}
                            {isEditing && (
                              <Box className={classes.inlineBenefitsEditor}>
                                {benefits.length > 0 && (
                                  <Box className={classes.benefitsList}>
                                    {benefits.map((b, idx) => (
                                      <Box
                                        key={`${b}-${idx}`}
                                        className={classes.benefitItem}
                                      >
                                        <IconButton
                                          className={
                                            classes.removeBenefitButton
                                          }
                                          onClick={() =>
                                            setBenefits(prev =>
                                              prev.filter((_, i) => i !== idx)
                                            )
                                          }
                                        >
                                          <Remove style={{ fontSize: 16 }} />
                                        </IconButton>
                                        <Typography
                                          className={classes.benefitText}
                                        >
                                          {b}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                                <Box className={classes.benefitsInputRow}>
                                  <Button
                                    className={classes.addBenefitsIconButton}
                                    onClick={handleAddBenefit}
                                  >
                                    <Add style={{ fontSize: 12 }} />
                                  </Button>
                                  <input
                                    className={classes.benefitInput}
                                    placeholder="Add more benefits"
                                    value={newBenefit}
                                    onChange={e =>
                                      setNewBenefit(e.target.value)
                                    }
                                    onKeyDown={e => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddBenefit();
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {(plan.price != null || plan.secondaryPrice != null) &&
                          !isEditing && (
                            <Box className={classes.subscriptionItemPriceRow}>
                              {plan.secondaryPrice != null && (
                                <Typography
                                  className={classes.subscriptionPriceBadge}
                                >
                                  <span
                                    className={classes.subscriptionPriceValue}
                                  >
                                    ${plan.secondaryPrice}
                                  </span>
                                  <span
                                    className={classes.subscriptionPricePeriod}
                                  >
                                    {" "}
                                    /{" "}
                                    {plan.secondaryPeriod === "month"
                                      ? "month"
                                      : "week"}
                                  </span>
                                </Typography>
                              )}
                              {plan.price != null && (
                                <Typography
                                  className={classes.subscriptionPriceBadge}
                                >
                                  <span
                                    className={classes.subscriptionPriceValue}
                                  >
                                    ${plan.price}
                                  </span>
                                  <span
                                    className={classes.subscriptionPricePeriod}
                                  >
                                    {" "}
                                    / {plan.period}
                                  </span>
                                </Typography>
                              )}
                            </Box>
                          )}

                        {isEditing && (
                          <Box className={classes.subscriptionItemPriceRow}>
                            {isMobile &&
                              <Typography
                                className={classes.subscriptionItemName}
                                style={{ fontSize: 14 }}
                              >
                                Price & Time Period
                              </Typography>
                            }
                            <Box>
                              <Box className={classes.priceRow}>
                                <TextField
                                  variant="outlined"
                                  type="number"
                                  value={form.price}
                                  onChange={e =>
                                    setForm(f => ({
                                      ...f,
                                      price: e.target.value,
                                    }))
                                  }
                                  placeholder="Price"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  className={`${classes.priceInput} ${classes.modalTextField} ${classes.priceInputHeight}`}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment
                                        position="start"
                                        style={{
                                          marginRight: "-4px",
                                          marginLeft: "-10px",
                                          color: "#ffffff",
                                        }}
                                      >
                                        <AttachMoney style={{ fontSize: isMobile ? 14 : 24 }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                <Box className={classes.chipGroup}>
                                  <Button
                                    className={`${classes.chip} ${primaryPeriod === "week"
                                      ? classes.chipActive
                                      : ""
                                      }`}
                                    onClick={() =>
                                      handlePrimaryPeriodChange("week")
                                    }
                                  >
                                    Week
                                  </Button>
                                  <Button
                                    className={`${classes.chip} ${primaryPeriod === "month"
                                      ? classes.chipActive
                                      : ""
                                      }`}
                                    onClick={() =>
                                      handlePrimaryPeriodChange("month")
                                    }
                                  >
                                    Month
                                  </Button>
                                </Box>
                              </Box>

                              {!showSecondPeriod && (
                                <Button
                                  className={classes.addPeriodButton}
                                  onClick={() => {
                                    setSecondaryPeriod(
                                      primaryPeriod === "week"
                                        ? "month"
                                        : "week"
                                    );
                                    setShowSecondPeriod(true);
                                  }}
                                >
                                  <Add style={{ fontSize: 18 }} />
                                  Add period
                                </Button>
                              )}

                              {showSecondPeriod && (
                                <Box
                                  className={classes.priceRow}
                                  style={{ marginTop: 12 }}
                                >
                                  <TextField
                                    variant="outlined"
                                    type="number"
                                    value={secondaryPrice}
                                    onChange={e =>
                                      setSecondaryPrice(e.target.value)
                                    }
                                    placeholder="Price"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    className={`${classes.priceInput} ${classes.modalTextField} ${classes.priceInputHeight}`}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment
                                          position="start"
                                          style={{
                                            marginRight: "-4px",
                                            marginLeft: "-10px",
                                            color: "#ffffff",
                                          }}
                                        >
                                          <AttachMoney style={{ fontSize: isMobile ? 14 : 24 }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                  <Box className={classes.chipGroup}>
                                    <Button
                                      className={`${classes.chip} ${secondaryPeriod === "week"
                                        ? classes.chipActive
                                        : ""
                                        }`}
                                      onClick={() =>
                                        handleSecondaryPeriodChange("week")
                                      }
                                    >
                                      Week
                                    </Button>
                                    <Button
                                      className={`${classes.chip} ${secondaryPeriod === "month"
                                        ? classes.chipActive
                                        : ""
                                        }`}
                                      onClick={() =>
                                        handleSecondaryPeriodChange("month")
                                      }
                                    >
                                      Month
                                    </Button>
                                  </Box>
                                  <IconButton
                                    className={classes.removePeriodButton}
                                    onClick={() => {
                                      setShowSecondPeriod(false);
                                      setSecondaryPrice("");
                                    }}
                                  >
                                    <TrashIcon
                                      width={24}
                                      height={24}
                                      style={{ color: "#FF78AA" }}
                                    />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box className={classes.subscriptionItemRight} style={isEditing ? { alignItems: "center", minWidth: isMobileSM ? "min-content" : "104px", margin: isMobileSM && !isEditing ? "0 0 auto 0" : "3% 0 auto 0" } : {}}>
                      <Box className={classes.subscriptionItemActions}>
                        {!isEditing && (
                          <Switch
                            color="primary"
                            size="small"
                            className={classes.subscriptionSwitch}
                            checked={!!plan.isActive}
                            onChange={() => handleToggleActive(plan)}
                          />
                        )}
                        <IconButton
                          size="small"
                          className={classes.subscriptionItemIconButton}
                          onClick={() =>
                            isEditing
                              ? handleSaveEditedPlan()
                              : handleEditPlan(plan)
                          }
                        >
                          <img
                            src={pencilIcon || "/placeholder.svg"}
                            width={isMobile ? 14 : 24}
                            height={isMobile ? 14 : 24}
                            alt="Edit subscription"
                          />
                        </IconButton>
                        {!isEditing && plan?.isDeletable !== false && (
                          <IconButton
                            size="small"
                            className={classes.subscriptionItemIconButton}
                            onClick={() => handleRequestDeletePlan(plan)}
                          >
                            <img
                              src={trashIconSBUS || "/placeholder.svg"}
                              width={isMobile ? 14 : 24}
                              height={isMobile ? 14 : 24}
                              alt="Delete subscription"
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {!isEditing && (
                      <Box
                        style={{
                          flexBasis: "100%",
                          width: "100%",
                          marginTop: 12,
                          paddingTop: 14,
                          borderTop: "1px solid #1F1F1F",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          boxSizing: "border-box",
                        }}
                      >
                        <Typography
                          className={clsx(classes.subscriptionItemLeft, classes.subscriptionItemTrialPlanLeft)}
                        >
                          Free trial
                        </Typography>
                        <Typography className={classes.subscriptionTrialStatus}
                        >
                          {plan.trialPeriod === "month" ? (
                            <>
                              <span style={{ fontWeight: 600 }}>Free</span>
                              <span style={{ fontWeight: 400 }}>
                                {" "}
                                / 1 month
                              </span>
                            </>
                          ) : plan.trialPeriod === "week" ? (
                            <>
                              <span style={{ fontWeight: 600 }}>Free</span>
                              <span style={{ fontWeight: 400 }}> / 1 week</span>
                            </>
                          ) : (
                            <span style={{ fontWeight: 600 }}>Off</span>
                          )}
                        </Typography>
                        <Box className={classes.subscriptionItemRight}
                        >
                          <Switch
                            color="primary"
                            size="small"
                            className={classes.subscriptionSwitch}
                            checked={
                              plan.trialPeriod === "week" ||
                              plan.trialPeriod === "month"
                            }
                            disabled={updatePlanMutation.isPending}
                            onChange={(_, checked) =>
                              handleTrialQuickToggle(plan, checked)
                            }
                          />
                        </Box>
                      </Box>
                    )}
                    {isEditing && (
                      <Box
                        style={{
                          flexBasis: "100%",
                          width: "100%",
                          marginTop: 12,
                          paddingTop: 14,
                          borderTop: "1px solid #1F1F1F",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          boxSizing: "border-box",
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          style={{
                            color: "#FFFFFF",
                            fontSize: 14,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          Free trial
                        </Typography>
                        <Box
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: isMobile ? "center" : "flex-end",
                          }}
                        >
                          <Box
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              background: "#1F1F1F",
                              borderRadius: 6,
                              padding: 2,
                              gap: 2,
                            }}
                          >
                            <Button
                              onClick={() => setTrialPeriod("week")}
                              style={{
                                ...TRIAL_SEGMENT_STYLE.week,
                                background:
                                  trialPeriod === "week"
                                    ? "#FE5A97"
                                    : "transparent",
                              }}
                            >
                              Week
                            </Button>
                            <Button
                              onClick={() => setTrialPeriod("month")}
                              style={{
                                ...TRIAL_SEGMENT_STYLE.month,
                                background:
                                  trialPeriod === "month"
                                    ? "#FE5A97"
                                    : "transparent",
                              }}
                            >
                              Month
                            </Button>
                            <Button
                              onClick={() => setTrialPeriod("off")}
                              style={{
                                ...TRIAL_SEGMENT_STYLE.off,
                                background:
                                  trialPeriod === "off"
                                    ? "#FE5A97"
                                    : "transparent",
                              }}
                            >
                              Off
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      <Box className={classes.tipsContainer}      >
        <Box>
          <Typography
            style={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: isMobile ? 16 : 24,
              lineHeight: isMobile ? "16px" : "24px",
              marginBottom: isMobile ? 8 : 12,
            }}
          >
            Tips
          </Typography>
          <Box
            style={{
              borderRadius: 8,
              backgroundColor: "transparent",
              maxWidth: 420,
            }}
          >
            <Typography
              style={{
                color: "#ffffff",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 400,
                marginBottom: isMobile ? 8 : 10,
              }}
            >
              Minimum tip amount
            </Typography>
            <TextField
              select
              variant="outlined"
              value={minimumTipAmount}
              disabled={pricingLoading || updatePricingMutation.isPending}
              onChange={e => saveMinimumTipAmount(Number(e.target.value))}
              className={`${classes.modalTextField} ${classes.modalInputHeight}`}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      backgroundColor: "#131313",
                      color: "#ffffff",
                    },
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                  >
                    <DollarSign />
                  </InputAdornment>
                ),
              }}
            >
              {tipAmountOptions.map(v => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box>
          <Typography
            style={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: isMobile ? 16 : 24,
              lineHeight: isMobile ? "16px" : "24px",
              marginBottom: isMobile ? 8 : 12,
            }}
          >
            Video requests
          </Typography>
          <Box
            style={{
              borderRadius: 8,
              backgroundColor: "transparent",
              maxWidth: 420,
            }}
          >
            <Typography
              style={{
                color: "#ffffff",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 400,
                marginBottom: isMobile ? 8 : 10,
              }}
            >
              Video request price
            </Typography>
            <TextField
              select
              variant="outlined"
              value={videoRequestPrice}
              disabled={pricingLoading || updatePricingMutation.isPending}
              onChange={e => saveVideoRequestPrice(Number(e.target.value))}
              className={`${classes.modalTextField} ${classes.modalInputHeight}`}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      backgroundColor: "#131313",
                      color: "#ffffff",
                    },
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                  >
                    <DollarSign />
                  </InputAdornment>
                ),
              }}
            >
              {videoPriceOptions.map(v => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            color: "#fff",
            borderRadius: "12px",
            width: "777px",
            maxWidth: "100%",
            overflow: "hidden",
            margin: "21px"
          },
        }}
      >
        <DialogTitle disableTypography className={classes.modalHeader}>
          <Typography className={classes.modalTitle}>
            Create new subscription type
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            className={classes.modalCloseButton}
          >
            <Close style={{ color: "#FFFFFF" }} />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: 0 }}>
          <Box
            display="flex"
            flexDirection="column"
            gridGap={20}
            className={classes.modalBody}
          >
            <Box>
              <Typography className={classes.modalLabel}>
                Subscription name
              </Typography>
              <TextField
                variant="outlined"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Create a Name of Your Subscription Type"
                fullWidth
                className={`${classes.modalTextField} ${classes.modalInputHeight}`}
              />
            </Box>

            <Box>
              <Typography className={classes.modalLabel}>
                Price &amp; Time Period
              </Typography>
              <Box className={classes.priceRow}>
                <TextField
                  variant="outlined"
                  type="number"
                  value={form.price}
                  onChange={e =>
                    setForm(f => ({ ...f, price: e.target.value }))
                  }
                  placeholder="Price"
                  inputProps={{ min: 0, step: 0.01 }}
                  className={`${classes.priceInput} ${classes.modalTextField} ${classes.priceInputHeight}`}
                  style={{ fontSize: isMobile ? 14 : 16 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        style={{
                          marginRight: "-2px",
                          marginLeft: "-5px",
                          color: "#ffffff",
                        }}
                      >
                        <AttachMoney style={{ fontSize: isMobile ? 14 : 24 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box className={classes.chipGroup}>
                  <Button
                    className={`${classes.chip} ${primaryPeriod === "week" ? classes.chipActive : ""
                      }`}
                    onClick={() => handlePrimaryPeriodChange("week")}
                  >
                    Week
                  </Button>
                  <Button
                    className={`${classes.chip} ${primaryPeriod === "month" ? classes.chipActive : ""
                      }`}
                    onClick={() => handlePrimaryPeriodChange("month")}
                  >
                    Month
                  </Button>
                </Box>
              </Box>

              {!showSecondPeriod && (
                <Button
                  className={classes.addPeriodButton}
                  onClick={() => {
                    // коли додаємо другий період, одразу ставимо протилежний до першого
                    setSecondaryPeriod(
                      primaryPeriod === "week" ? "month" : "week"
                    );
                    setShowSecondPeriod(true);
                  }}
                >
                  <Add style={{ fontSize: 18 }} />
                  Add period
                </Button>
              )}

              {showSecondPeriod && (
                <Box className={classes.priceRow} style={{ marginTop: 12 }}>
                  <TextField
                    variant="outlined"
                    type="number"
                    value={secondaryPrice}
                    onChange={e => setSecondaryPrice(e.target.value)}
                    placeholder="Price"
                    inputProps={{ min: 0, step: 0.01 }}
                    className={`${classes.priceInput} ${classes.modalTextField} ${classes.priceInputHeight}`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          style={{
                            marginRight: "-2px",
                            marginLeft: "-5px",
                            color: "#ffffff",
                          }}
                        >
                          <AttachMoney style={{ fontSize: isMobile ? 14 : 24 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box className={classes.chipGroup}>
                    <Button
                      className={`${classes.chip} ${secondaryPeriod === "week" ? classes.chipActive : ""
                        }`}
                      onClick={() => handleSecondaryPeriodChange("week")}
                    >
                      Week
                    </Button>
                    <Button
                      className={`${classes.chip} ${secondaryPeriod === "month" ? classes.chipActive : ""
                        }`}
                      onClick={() => handleSecondaryPeriodChange("month")}
                    >
                      Month
                    </Button>
                  </Box>
                  <IconButton
                    className={classes.removePeriodButton}
                    onClick={() => {
                      setShowSecondPeriod(false);
                      setSecondaryPrice("");
                    }}
                  >
                    <TrashIcon
                      width={24}
                      height={24}
                      style={{ color: "#FF78AA" }}
                    />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Box style={{ display: "flex", flexDirection: isMobile ? "column-reverse" : "column", gap: 32 }}>
              <Box>
                <Typography className={classes.memberBenefitsTitle}>
                  Member benefits
                </Typography>
                <Typography className={classes.memberBenefitsSubtitle}>
                  Add included benefits and accesses for your subscribers
                </Typography>

                {benefits.length > 0 && (
                  <Box className={classes.benefitsList} style={{ gap: 12 }}>
                    {benefits.map((b, idx) => (
                      <Box key={`${b}-${idx}`} className={classes.benefitItem}>
                        <IconButton
                          className={classes.removeBenefitButton}
                          onClick={() =>
                            setBenefits(prev => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Remove style={{ fontSize: 12, color: "#FF78AA" }} />
                        </IconButton>
                        <Typography className={classes.benefitText}>
                          {b}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box className={classes.benefitsInputRow} style={{ marginTop: 12 }}>
                  <Button
                    className={classes.addBenefitsIconButton} style={{ backgroundColor: "#FF78AA" }}
                    onClick={handleAddBenefit}
                  >
                    <Add style={{ fontSize: 18 }} />
                  </Button>
                  <input
                    className={classes.benefitInput}
                    placeholder="Add benefit"
                    value={newBenefit}
                    onChange={e => setNewBenefit(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddBenefit();
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  className={classes.memberBenefitsTitle}
                  style={{ marginBottom: 0 }}
                >
                  Free trial
                </Typography>
                <Box
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    background: "#1F1F1F",
                    borderRadius: 6,
                    padding: 2,
                    gap: 2,
                    flexShrink: 0,
                  }}
                >
                  <Button
                    onClick={() => setTrialPeriod("week")}
                    style={{
                      ...TRIAL_SEGMENT_STYLE.week,
                      background:
                        trialPeriod === "week" ? "#FE5A97" : "transparent",
                    }}
                  >
                    Week
                  </Button>
                  <Button
                    onClick={() => setTrialPeriod("month")}
                    style={{
                      ...TRIAL_SEGMENT_STYLE.month,
                      background:
                        trialPeriod === "month" ? "#FE5A97" : "transparent",
                    }}
                  >
                    Month
                  </Button>
                  <Button
                    onClick={() => setTrialPeriod("off")}
                    style={{
                      ...TRIAL_SEGMENT_STYLE.off,
                      background:
                        trialPeriod === "off" ? "#FE5A97" : "transparent",
                    }}
                  >
                    Off
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <Button
            onClick={handleCloseModal}
            disabled={createMutation.isPending}
            className={`${classes.modalButton} ${classes.modalButtonSecondary}`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePlan}
            disabled={createMutation.isPending || updatePlanMutation.isPending}
            className={`${classes.modalButton} ${classes.modalButtonPrimary}`}
          >
            {createMutation.isPending ? "Creating…" : "Create subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(planPendingDelete)}
        onClose={() => {
          if (deletePlanMutation.isPending) return;
          handleCloseDeletePlanDialog();
        }}
        maxWidth={false}
        PaperProps={{ className: classes.deleteConfirmPaper }}
      >
        <Box className={classes.deleteConfirmHeader}>
          <Box>
            <Typography className={classes.deleteConfirmTitle} component="div">
              Are you sure you want to delete this subscription?
            </Typography>
            <Typography
              className={classes.deleteConfirmSubtitle}
              component="div"
            >
              You can temporarily turn it off or edit the price and benefits
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDeletePlanDialog}
            disabled={deletePlanMutation.isPending}
            className={classes.modalCloseButton}
            aria-label="Close"
          >
            <Close style={{ color: "#FFFFFF" }} />
          </IconButton>
        </Box>

        {planPendingDelete && (
          <DialogContent style={{ padding: 0 }}>
            <Box className={classes.deleteConfirmBody}>
              <Box className={classes.deleteConfirmSummary}>
                <Box className={classes.deleteConfirmSummaryLeft}>
                  <Typography className={classes.deleteConfirmPlanName}>
                    {String(planPendingDelete.name || "").trim() || "—"}
                  </Typography>
                  <Box>
                    {(Array.isArray(planPendingDelete.memberBenefits)
                      ? planPendingDelete.memberBenefits
                      : []
                    ).map((benefit: string, idx: number) => (
                      <Box
                        key={`${benefit}-${idx}`}
                        className={classes.deleteConfirmBenefitRow}
                      >
                        <img
                          src={markIcon || "/placeholder.svg"}
                          alt=""
                          className={classes.deleteConfirmBenefitMark}
                        />
                        <Typography
                          className={classes.deleteConfirmBenefitText}
                          component="span"
                        >
                          {benefit}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box
                  className={classes.deleteConfirmSummaryDivider}
                  aria-hidden
                />
                <Box className={classes.deleteConfirmSummaryRight}>
                  {planPendingDelete.secondaryPrice != null && (
                    <Typography className={classes.deleteConfirmPriceLine}>
                      ${planPendingDelete.secondaryPrice}
                      <span style={{ fontWeight: 400 }}>
                        {" "}
                        /
                        {planPendingDelete.secondaryPeriod === "month"
                          ? " month"
                          : " week"}
                      </span>
                    </Typography>
                  )}
                  {planPendingDelete.price != null && (
                    <Typography className={classes.deleteConfirmPriceLine}>
                      ${planPendingDelete.price}
                      <span style={{ fontWeight: 400 }}>
                        {" "}
                        /{planPendingDelete.period === "month" ? " month" : " week"}
                      </span>
                    </Typography>
                  )}
                  {planPendingDelete.price == null &&
                    planPendingDelete.secondaryPrice == null && (
                      <Typography
                        className={classes.deleteConfirmPriceLine}
                        style={{ fontWeight: 400, fontSize: 15 }}
                      >
                        —
                      </Typography>
                    )}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        )}

        <DialogActions className={classes.deleteConfirmActions}>
          <Button
            onClick={handleCloseDeletePlanDialog}
            disabled={deletePlanMutation.isPending}
            className={`${classes.modalButton} ${classes.modalButtonSecondary}`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeletePlan}
            disabled={deletePlanMutation.isPending}
            className={`${classes.modalButton} ${classes.modalButtonPrimary}`}
          >
            {deletePlanMutation.isPending
              ? "Deleting…"
              : "Delete subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
};

export default Monetization;
